import { UseQueryOptions } from 'react-query';
import { HttpError } from '@frontend/fetch';
import { useQuery } from '@frontend/react-query-helpers';
import { SchemaPlatformLocationFeature } from '@frontend/schema';
import {
  BundleTrialStatusRequest,
  BundleTrialStatusResponse,
  GetBundleUpgradeEligibilityRequest,
  GetBundleUpgradeEligibilityResponse,
  GetTrialHistoryForMultiRequest,
  GetTrialHistoryForMultiResponse,
} from './types';

const HOURS_IN_MS = 1000 * 60 * 60;

export const freeTrialSubscriptionQueryKeys = {
  subscriptionBaseQuery: ['subscription', 'feature'],
  trialExpiringFeatures: ({ locationIds }: GetTrialHistoryForMultiRequest) => [
    ...freeTrialSubscriptionQueryKeys.subscriptionBaseQuery,
    'free-trial',
    'trial-expiring',
    ...locationIds,
  ],
  bundleUpgradeEligibility: ({ locationIds }: GetBundleUpgradeEligibilityRequest) => [
    ...freeTrialSubscriptionQueryKeys.subscriptionBaseQuery,
    'bundle-upgrade',
    'eligibility',
    ...locationIds,
  ],
  bundleTrialStatus: ({ locationIds }: BundleTrialStatusRequest) => [
    ...freeTrialSubscriptionQueryKeys.subscriptionBaseQuery,
    'bundle-trial',
    'status',
    ...locationIds,
  ],
};

export const useTrialExpiringFeatures = (
  request: GetTrialHistoryForMultiRequest,
  options?: UseQueryOptions<GetTrialHistoryForMultiResponse>
) => {
  return useQuery({
    queryFn: async () => await SchemaPlatformLocationFeature.GetTrialHistoryForMulti(request),
    queryKey: freeTrialSubscriptionQueryKeys.trialExpiringFeatures(request),
    cacheTime: 12 * HOURS_IN_MS,
    staleTime: 12 * HOURS_IN_MS,
    ...options,
    enabled: !!request.locationIds.length && (options?.enabled ?? true),
  });
};

export function useGetBundleUpgradeEligibility<TData = GetBundleUpgradeEligibilityResponse>(
  request: GetBundleUpgradeEligibilityRequest,
  options?: UseQueryOptions<GetBundleUpgradeEligibilityResponse, HttpError, TData>
) {
  return useQuery({
    queryFn: async () => await SchemaPlatformLocationFeature.AreLocationsEligibleForBundleTrial(request),
    queryKey: freeTrialSubscriptionQueryKeys.bundleUpgradeEligibility(request),
    cacheTime: 12 * HOURS_IN_MS,
    staleTime: 12 * HOURS_IN_MS,
    ...options,
    retry: false,
    enabled: request.locationIds?.length > 0 && (options?.enabled ?? true),
  });
}

export function useGetBundleTrialStatus<TData = BundleTrialStatusResponse>(
  request: BundleTrialStatusRequest,
  options?: UseQueryOptions<BundleTrialStatusResponse, HttpError, TData>
) {
  return useQuery({
    queryFn: async () =>
      await SchemaPlatformLocationFeature.BundleTrialStatus(request).then((response) => {
        return {
          ...response,
          maxTrialEndDate: response.bundleTrialStatus
            ?.filter(({ isBundleTrialActive }) => !!isBundleTrialActive)
            ?.map(({ featureDetails = [] }) =>
              featureDetails.map(({ endDate }) => (endDate ? new Date(endDate) : undefined))
            )
            .flat()
            .sort((a, b) => (b?.getTime() ?? 0) - (a?.getTime() ?? 0))[0],
        };
      }),
    queryKey: freeTrialSubscriptionQueryKeys.bundleTrialStatus(request),
    cacheTime: 12 * HOURS_IN_MS,
    staleTime: 12 * HOURS_IN_MS,
    ...options,
    retry: false,
    enabled: request.locationIds?.length > 0 && (options?.enabled ?? true),
  });
}
